<script setup>
import { computed, watch, getCurrentInstance } from 'vue';
import { useAsyncState } from '@vueuse/core';
import Form from '../form/Form';
import FormSaveBtn from '../form/FormSaveBtn';
import Button from "../Button.vue";

const props = defineProps({
    modal: Object
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;
const tableName = 'modulestructure';

// --- State & Async ---

const activeEditId = computed(() => store.state.componentFields.activeEditId);
const componentId = computed(() => store.state.componentFields.componentId);
const items = computed(() => store.state.componentFields.items);

// Fetch the item being edited
const { state: item, execute: fetchItem } = useAsyncState(
    () => store.dispatch('itemData/get', {
        tableName: tableName,
        id: activeEditId.value
    }),
    null,
    { immediate: true }
);

// --- Computed ---

const formId = computed(() => {
    if (item.value) {
        const id = item.value.id || 0;
        return `form:${tableName}:${id}`;
    }
    return null;
});

const titleValue = computed(() => {
    if (formId.value && store.state[formId.value]) {
        return store.state[formId.value].currentData.title;
    }
    return null;
});

// --- Methods ---

const createForm = () => {
    if (!item.value) return;

    const fieldNames = store.getters['componentFields/getFields'](item.value.type);

    if (props.modal) {
        props.modal.formId = formId.value;
    }

    store.dispatch('forms/createForm', {
        tableName: tableName,
        id: activeEditId.value,
        fieldNames: fieldNames,
        onInit: () => {
            const fields = store.state[formId.value].fields;
            const field = fields.find(f => f.columnName === 'title');
            if (field) {
                field.label = item.value.type === 'fieldset' ? 'Legend' : 'Label';
            }

            if (activeEditId.value === 0) {
                const presets = [
                    { name: 'modulecomponentsID', value: componentId.value }, // todo Deprecated
                    { name: 'modulecomponentsId', value: componentId.value },
                    { name: 'browser', value: 1 }
                ];

                if (store.state.componentFields.activeFieldsetId) {
                    presets.push({
                        name: 'fieldsetId',
                        value: store.state.componentFields.activeFieldsetId
                    });
                }

                presets.forEach(p => {
                    store.commit(`${formId.value}/presetData`, p);
                });
            }
        }
    });

    // Provide modifiedData getter to the service
    store.state.componentFields.getModifiedData = () => {
        return store.getters[`${formId.value}/modifiedData`]();
    };
};

const confirmDelete = (e, itemObj) => {
    e.stopPropagation();
    store.dispatch('modals/show', {
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Are you sure?',
            modalBody: `
                <div class="alert alert-danger">This action will result in loss of data.</div>
                <p>This action <strong>cannot</strong> be undone. This will permanently delete all data from the <strong>${itemObj.title}</strong> field.</p>
            `,
            confirmStr: itemObj.title,
            onConfirm: () => {
                store.dispatch('componentFields/deleteField', {
                    fieldId: itemObj.id,
                    router: proxy.$router
                });
            }
        }
    });
};

// --- Watchers ---

// Re-fetch item when the active ID changes
watch(activeEditId, () => fetchItem());

// Create the form once formId is established or changed
watch(formId, (newId) => {
    if (newId) createForm();
}, { immediate: true });

// Sync display order when items list changes
watch(items, (newItems) => {
    if (formId.value) {
        store.commit(`${formId.value}/presetData`, {
            name: 'displayOrder',
            value: newItems.length + 1,
        });
    }
}, { immediate: true });

// Validation logic for the 'title' field
watch(titleValue, (val) => {
    if (!formId.value) return;

    store.commit(`${formId.value}/setFieldProperty`, {
        fieldName: 'title',
        property: 'error',
        value: ''
    });

    if (val) {
        const el = document.querySelector('[name="title"]');
        let hasError = false;

        items.value.forEach((i) => {
            if (i.id !== activeEditId.value && i.title === val) {
                hasError = true;
                el?.setCustomValidity('Label in use.');
                el?.reportValidity();
            }
        });

        if (!hasError && el) {
            el.setCustomValidity('');
            el.reportValidity();
        }
    }
});
</script>

<template>
    <div v-if="item">
        <Form v-if="formId" :form-id="formId" :show-legends="true"/>

        <div class="clearfix text-center mt-3">
            <FormSaveBtn :button-text="'Save'" :form-id="formId"/>

            <Button v-if="item.id" @click.native="confirmDelete($event, item)">
                Delete
            </Button>
        </div>
    </div>
</template>